<p-toast></p-toast>

<app-page-shell>

    <app-page-hero
        eyebrow="Fleet Mobilization"
        title="Mobilization Control"
        subtitle="Assign drivers and vehicles to Logistics Projects or manual project sites, then track approval and activation from one workspace.">
        <div nav>
            <p-button label="Overview" icon="pi pi-chart-line" routerLink="/fleet/mobilization"></p-button>
            <p-button label="Entries" icon="pi pi-list" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/entries"></p-button>
            <p-button label="Planning" icon="pi pi-route" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/planning"></p-button>
            <p-button label="Approvals" icon="pi pi-check-square" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/change-requests"></p-button>
            <p-button label="History" icon="pi pi-history" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/history"></p-button>
        </div>
        <div actions>
            <p-button label="New Mobilization" icon="pi pi-plus" routerLink="/fleet/mobilization/new"></p-button>
        </div>
    </app-page-hero>

    <app-page-search-panel
        title="Mobilization Search"
        [activeFilters]="activeFilterCount()"
        [resultText]="totalRecords + ' records found'"
        [collapsed]="false"
        (search)="loadMobilizations()"
        (clear)="clearSearch()">
        <div class="search-grid">
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input id="overview-keyword" pInputText [(ngModel)]="keyword" placeholder="Driver, vehicle, project, site" />
                        <label for="overview-keyword">Keyword</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="overview-status" [options]="statusOptions" [(ngModel)]="selectedStatus" optionLabel="label" optionValue="value" [showClear]="true" placeholder="Any status" styleClass="w-full"></p-select>
                        <label for="overview-status">Status</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-datePicker inputId="overview-dates" [(ngModel)]="dateRange" selectionMode="range" [readonlyInput]="true" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
                        <label for="overview-dates">Start Date Range</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="overview-vehicle-type" [options]="vehicleTypes" [(ngModel)]="vehicleTypeId" optionLabel="name" optionValue="id"
                            [showClear]="true" [filter]="true" filterBy="name" placeholder="Any truck type" appendTo="body" styleClass="w-full"></p-select>
                        <label for="overview-vehicle-type">Truck Type</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-ticket"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="overview-trailer-type" [options]="trailerTypes" [(ngModel)]="trailerTypeId" optionLabel="name" optionValue="trailerTypeId"
                            [showClear]="true" [filter]="true" filterBy="code,name" placeholder="Any trailer type" appendTo="body" styleClass="w-full"></p-select>
                        <label for="overview-trailer-type">Trailer Type</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <app-logistics-project-lookup inputId="overview-logistics-project" placeholder="Any project" [(ngModel)]="logisticsProjectId"></app-logistics-project-lookup>
                        <label for="overview-logistics-project">Logistics Project</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
        </div>
    </app-page-search-panel>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <div class="dashboard-grid">
        <app-page-data-panel title="Daily Mobilization" subtitle="Daily counts and salary totals for the selected date range." [resultText]="(dashboard?.dailyRows?.length || 0) + ' days'">
            <p-table [value]="dashboard?.dailyRows || []" [loading]="isLoading" styleClass="p-datatable-sm" responsiveLayout="scroll">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Date</th>
                        <th>Mobilizations</th>
                        <th>Active</th>
                        <th>Trailers</th>
                        <th>Salary</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>{{ dateLabel(row.date) }}</td>
                        <td>{{ row.mobilizationCount ?? 0 }}</td>
                        <td>{{ row.activeMobilizationCount ?? 0 }}</td>
                        <td>{{ row.trailerAssignedCount ?? 0 }}</td>
                        <td>{{ amountLabel(row.salaryTotalAmount) }}</td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="5" class="empty-cell">No daily mobilization data.</td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>

        <app-page-data-panel title="Status Breakdown" subtitle="Assignment lifecycle counts and salary totals." [resultText]="(dashboard?.statusBreakdown?.length || 0) + ' statuses'">
            <p-table [value]="dashboard?.statusBreakdown || []" [loading]="isLoading" styleClass="p-datatable-sm" responsiveLayout="scroll">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Status</th>
                        <th>Count</th>
                        <th>Salary</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>{{ row.label || row.key || '-' }}</td>
                        <td>{{ row.count ?? 0 }}</td>
                        <td>{{ amountLabel(row.salaryTotalAmount) }}</td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="3" class="empty-cell">No status breakdown.</td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>
    </div>

    <div class="dashboard-grid">
        <app-page-data-panel title="Truck Type Breakdown" subtitle="Mobilizations grouped by selected truck type snapshot.">
            <p-table [value]="dashboard?.vehicleTypeBreakdown || []" [loading]="isLoading" styleClass="p-datatable-sm" responsiveLayout="scroll">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Truck Type</th>
                        <th>Count</th>
                        <th>Salary</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>{{ row.label || row.key || '-' }}</td>
                        <td>{{ row.count ?? 0 }}</td>
                        <td>{{ amountLabel(row.salaryTotalAmount) }}</td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="3" class="empty-cell">No truck type breakdown.</td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>

        <app-page-data-panel title="Trailer Type Breakdown" subtitle="Mobilizations grouped by selected trailer type snapshot.">
            <p-table [value]="dashboard?.trailerTypeBreakdown || []" [loading]="isLoading" styleClass="p-datatable-sm" responsiveLayout="scroll">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Trailer Type</th>
                        <th>Count</th>
                        <th>Salary</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>{{ row.label || row.key || '-' }}</td>
                        <td>{{ row.count ?? 0 }}</td>
                        <td>{{ amountLabel(row.salaryTotalAmount) }}</td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="3" class="empty-cell">No trailer type breakdown.</td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>
    </div>

    <div class="dashboard-grid">
        <app-page-data-panel title="Active Mobilizations" subtitle="Assignments currently running on-site." [resultText]="activeMobilizations().length + ' active'">
            <p-table [value]="activeMobilizations()" [loading]="isLoading" styleClass="p-datatable-sm" responsiveLayout="scroll">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Assignment</th>
                        <th>Driver</th>
                        <th>Truck</th>
                        <th>Trailer</th>
                        <th>Project/Site</th>
                        <th>Dates</th>
                        <th>Salary</th>
                        <th></th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td class="font-semibold">{{ row.mobilizationNumber || ('MOB-' + row.mobilizationId) }}</td>
                        <td>{{ row.driverName || '-' }}</td>
                        <td>{{ row.vehiclePlateNumber || row.vehicleRegistrationNumber || '-' }}</td>
                        <td>{{ trailerLabel(row) }}</td>
                        <td>
                            <div class="stacked-cell">
                                <span>{{ projectLabel(row) }}</span>
                                <small>{{ siteLabel(row) }}</small>
                            </div>
                        </td>
                        <td>{{ dateLabel(row.startDate) }} - {{ dateLabel(row.endDate) }}</td>
                        <td>{{ salaryLabel(row) }}</td>
                        <td class="text-right">
                            <p-button icon="pi pi-arrow-right" [rounded]="true" [text]="true" (onClick)="openMobilization(row)" ariaLabel="Open mobilization"></p-button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="8" class="empty-cell">No active mobilizations.</td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>

        <app-page-data-panel title="Approval Queue" subtitle="Submitted or rejected assignments that need review." [resultText]="approvalQueue().length + ' items'">
            <p-table [value]="approvalQueue()" [loading]="isLoading" styleClass="p-datatable-sm" responsiveLayout="scroll">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Assignment</th>
                        <th>Status</th>
                        <th>Driver</th>
                        <th>Project</th>
                        <th>Trailer</th>
                        <th>Salary</th>
                        <th></th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td class="font-semibold">{{ row.mobilizationNumber || ('MOB-' + row.mobilizationId) }}</td>
                        <td><p-tag [value]="row.status" [severity]="statusSeverity(row.status)"></p-tag></td>
                        <td>{{ row.driverName || '-' }}</td>
                        <td>{{ projectLabel(row) }}</td>
                        <td>{{ trailerLabel(row) }}</td>
                        <td>{{ salaryLabel(row) }}</td>
                        <td class="text-right">
                            <p-button icon="pi pi-arrow-right" [rounded]="true" [text]="true" (onClick)="openMobilization(row)" ariaLabel="Open mobilization"></p-button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="7" class="empty-cell">No approval items.</td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>
    </div>

    <app-page-data-panel title="Expiring Assignments" subtitle="Active mobilizations ending within the next 7 days." [resultText]="expiringSoon().length + ' expiring'">
        <p-table [value]="expiringSoon()" [loading]="isLoading" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header">
            <tr>
                <th>Assignment</th>
                <th>Driver</th>
                <th>Truck</th>
                <th>Trailer</th>
                <th>Project/Site</th>
                <th>End Date</th>
                <th>Salary</th>
                <th></th>
            </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
                <tr>
                <td class="font-semibold">{{ row.mobilizationNumber || ('MOB-' + row.mobilizationId) }}</td>
                <td>{{ row.driverName || '-' }}</td>
                <td>{{ row.vehiclePlateNumber || row.vehicleRegistrationNumber || '-' }}</td>
                <td>{{ trailerLabel(row) }}</td>
                <td>{{ projectLabel(row) }}</td>
                <td>{{ dateLabel(row.endDate) }}</td>
                <td>{{ salaryLabel(row) }}</td>
                <td class="text-right">
                        <p-button label="Open" icon="pi pi-arrow-up-right" [text]="true" (onClick)="openMobilization(row)"></p-button>
                    </td>
                </tr>
            </ng-template>
        <ng-template pTemplate="emptymessage">
            <tr>
                <td colspan="8" class="empty-cell">No assignments are expiring in the next 7 days.</td>
            </tr>
        </ng-template>
        </p-table>
    </app-page-data-panel>
</app-page-shell>
